// # Security Settings Tab Component
import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../../auth/state';
import { auth } from '../../../auth/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { functions } from '../../../auth/firebase';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import './security_settings.css';

// # Add date-fns for date formatting
import { format, formatDistanceToNow } from 'date-fns';

// # Add Firestore imports
import { db } from '../../../auth/firebase';
import { collection, doc, getDoc } from 'firebase/firestore';

// # Add device detection helper
const getDeviceInfo = (userAgent) => {
  if (!userAgent) return { deviceType: 'Unknown Device', browser: 'Unknown Browser' };
  
  const ua = userAgent.toLowerCase();
  
  // # Helper to extract OS version
  const getOSVersion = (regex) => {
    const match = ua.match(regex);
    return match ? match[1] : '';
  };

  // # Device type detection
  const getDeviceType = () => {
    // # iOS devices with version
    if (/(iphone|ipod|ipad)/i.test(ua)) {
      const osVersion = getOSVersion(/cpu(?: iphone| ipad)? os (\d+_\d+(?:_\d+)?)/i)?.replace(/_/g, '.');
      
      if (/(iphone)/i.test(ua)) {
        return `iPhone (iOS ${osVersion})`;
      } else if (/(ipod)/i.test(ua)) {
        return `iPod (iOS ${osVersion})`;
      } else if (/(ipad)/i.test(ua)) {
        return `iPad (iOS ${osVersion})`;
      }
    }
    
    // # Android devices
    else if (/android/i.test(ua)) {
      const androidVersion = getOSVersion(/android (\d+(?:\.\d+)?)/i);
      
      // # Samsung devices
      if (/samsung/i.test(ua)) {
        const samsungMatch = ua.match(/samsung\s+(sm-[a-z0-9]+)/i);
        if (samsungMatch) {
          const model = samsungMatch[1].toUpperCase();
          // # Samsung model mappings
          const samsungModels = {
            'SM-G973U': 'Galaxy S10',
            'SM-G975U': 'Galaxy S10+',
            'SM-G970U': 'Galaxy S10e',
            'SM-G981U': 'Galaxy S20',
            'SM-G986U': 'Galaxy S20+',
            'SM-G988U': 'Galaxy S20 Ultra',
            'SM-G991U': 'Galaxy S21',
            'SM-G996U': 'Galaxy S21+',
            'SM-G998U': 'Galaxy S21 Ultra'
          };
          const deviceName = samsungModels[model] || model;
          return `Samsung ${deviceName} (Android ${androidVersion})`;
        }
        return `Samsung Device (Android ${androidVersion})`;
      }

      // # Other Android devices
      const modelMatch = ua.match(/android.*?;\s*(.*?)(?:build|[);])/i);
      if (modelMatch && modelMatch[1]) {
        const model = modelMatch[1]
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/[_]/g, ' ')
          .split(';')[0];
        
        // # Manufacturer mappings
        const manufacturers = {
          'samsung': 'Samsung',
          'lg': 'LG',
          'motorola': 'Motorola',
          'huawei': 'Huawei',
          'xiaomi': 'Xiaomi',
          'oneplus': 'OnePlus',
          'google': 'Google',
          'sony': 'Sony',
          'asus': 'Asus',
          'nokia': 'Nokia',
          'oppo': 'OPPO',
          'vivo': 'Vivo',
          'realme': 'Realme'
        };
        
        for (const [key, value] of Object.entries(manufacturers)) {
          if (model.toLowerCase().includes(key)) {
            return `${value} ${model.replace(new RegExp(key, 'i'), '').trim()} (Android ${androidVersion})`;
          }
        }
        return `${model} (Android ${androidVersion})`;
      }
      
      return ua.includes('mobile') ? 
        `Android Mobile (${androidVersion})` : 
        `Android Tablet (${androidVersion})`;
    }
    
    // # macOS
    else if (/macintosh|mac os x/i.test(ua) && !/(iphone|ipad|ipod)/i.test(ua)) {
      const macVersion = getOSVersion(/mac os x (\d+[._]\d+[._]\d+)/i)?.replace(/_/g, '.');
      return macVersion ? `macOS ${macVersion}` : 'macOS';
    }
    
    // # Windows
    else if (/windows/i.test(ua)) {
      if (ua.includes('windows nt 10')) return 'Windows 10/11';
      if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
      if (ua.includes('windows nt 6.2')) return 'Windows 8';
      if (ua.includes('windows nt 6.1')) return 'Windows 7';
      return 'Windows';
    }
    
    // # Linux
    else if (/linux/i.test(ua) && !/android/i.test(ua)) {
      return 'Linux';
    }
    
    return 'Unknown Device';
  };

  // # Browser detection
  const getBrowserInfo = () => {
    // # Version extraction helper
    const getVersion = (regex) => {
      const match = ua.match(regex);
      return match ? ` ${match[1].split('.')[0]}` : '';
    };

    // # Safari detection
    if (/safari/i.test(ua) && !/chrome|chromium|edg|firefox/i.test(ua)) {
      if (/(iphone|ipad|ipod)/i.test(ua)) {
        const version = getVersion(/version\/(\d+)/i);
        return `Mobile Safari${version}`;
      }
      const version = getVersion(/version\/(\d+)/i);
      return `Safari${version}`;
    }
    
    // # Edge
    if (/edg/i.test(ua)) {
      const version = getVersion(/edg\/(\d+)/i);
      return `Edge${version}`;
    }
    
    // # Chrome and variants
    if (/chrome/i.test(ua)) {
      if (/(iphone|ipad|ipod)/i.test(ua)) {
        const version = getVersion(/crios\/(\d+)/i);
        return `Chrome iOS${version}`;
      }
      if (/samsungbrowser/i.test(ua)) {
        const version = getVersion(/samsungbrowser\/(\d+)/i);
        return `Samsung Browser${version}`;
      }
      const version = getVersion(/chrome\/(\d+)/i);
      return `Chrome${version}`;
    }
    
    // # Firefox
    if (/firefox/i.test(ua)) {
      if (/(iphone|ipad|ipod)/i.test(ua)) {
        const version = getVersion(/fxios\/(\d+)/i);
        return `Firefox iOS${version}`;
      }
      const version = getVersion(/firefox\/(\d+)/i);
      return `Firefox${version}`;
    }
    
    // # Opera
    if (/opera|opr\//i.test(ua)) {
      const version = getVersion(/(?:opera|opr)\/(\d+)/i);
      return `Opera${version}`;
    }
    
    return 'Unknown Browser';
  };

  return {
    deviceType: getDeviceType(),
    browser: getBrowserInfo()
  };
};

function SecuritySettings({ isDarkMode }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    emailCurrentPassword: '',
    newEmail: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  // # Add state for login history
  const [loginHistory, setLoginHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // # Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (!isAuthenticated || !user) {
          navigate('/signin', { 
            replace: true,
            state: { from: '/settings/security' }
          });
          return;
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/signin');
      }
    };

    checkAuth();
  }, [isAuthenticated, user, navigate]);

  // # Fetch login history
  useEffect(() => {
    const fetchLoginHistory = async () => {
      if (!user) return;
      
      setIsLoadingHistory(true);
      setHistoryError('');
      
      try {
        // # Get current month in YYYY_MM format
        const now = new Date();
        const currentMonth = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
        const lastMonth = `${now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()}_${(now.getMonth() === 0 ? 12 : now.getMonth()).toString().padStart(2, '0')}`;
        
        // # Get user's activity document
        const activityDoc = await getDoc(doc(db, 'activities', user.uid));
        
        if (!activityDoc.exists()) {
          setLoginHistory([]);
          return;
        }

        const data = activityDoc.data();
        const history = [];

        // # Process current month's data
        if (data[currentMonth]?.auth_history) {
          const { sign_ins = {}, sign_outs = {} } = data[currentMonth].auth_history;
          
          // # Add sign-ins
          Object.entries(sign_ins).forEach(([id, signIn]) => {
            history.push({
              type: 'sign_in',
              ...signIn
            });
          });

          // # Add sign-outs
          Object.entries(sign_outs).forEach(([id, signOut]) => {
            history.push({
              type: 'sign_out',
              ...signOut
            });
          });
        }

        // # Process last month's data
        if (data[lastMonth]?.auth_history) {
          const { sign_ins = {}, sign_outs = {} } = data[lastMonth].auth_history;
          
          // # Add sign-ins
          Object.entries(sign_ins).forEach(([id, signIn]) => {
            history.push({
              type: 'sign_in',
              ...signIn
            });
          });

          // # Add sign-outs
          Object.entries(sign_outs).forEach(([id, signOut]) => {
            history.push({
              type: 'sign_out',
              ...signOut
            });
          });
        }

        // # Sort by timestamp descending
        history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // # Limit to last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const recentHistory = history.filter(item => 
          new Date(item.timestamp) > thirtyDaysAgo
        );

        setLoginHistory(recentHistory);
        
      } catch (error) {
        console.error('Error fetching login history:', error);
        setHistoryError('Failed to load login history. Please try again later.');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    fetchLoginHistory();
  }, [user]);

  // # If still loading or no user, show loading state
  if (isLoading || !user) {
    return (
      <div className={`sec_st_section ${isDarkMode ? 'stng_mn_dark-mode' : ''}`}>
        <div className="sec_st_loading">
          <p>Loading security settings...</p>
        </div>
      </div>
    );
  }

  // # Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    if (name.includes('Password')) {
      setError('');
      setSuccess('');
    } else {
      setEmailError('');
      setEmailSuccess('');
    }
  };

  // # Validate password
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase || !hasLowerCase) {
      return 'Password must contain both uppercase and lowercase letters';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  // # Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);
    setError('');
    setSuccess('');

    try {
      // # Validate new password
      if (formData.newPassword !== formData.confirmPassword) {
        throw new Error('New passwords do not match');
      }

      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        throw new Error(passwordError);
      }

      // # Reauthenticate user
      const credential = EmailAuthProvider.credential(
        user.email,
        formData.currentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // # Update password
      await updatePassword(auth.currentUser, formData.newPassword);

      // # Clear form and show success message
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSuccess('Password updated successfully');

    } catch (error) {
      console.error('Error updating password:', error);
      
      // # Handle specific Firebase errors
      if (error.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Please sign in again before changing your password');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later');
      } else {
        setError(error.message || 'Failed to update password');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // # Handle email change form submit
  const handleEmailFormSubmit = (e) => {
    e.preventDefault();
    setEmailError('');
    setShowEmailModal(true);
  };

  // # Handle email change confirmation
  const handleEmailChange = async () => {
    setIsChangingEmail(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      // # Get current user's email for reauthentication
      const currentEmail = auth.currentUser?.email;
      if (!currentEmail) {
        throw new Error('Current user email not found');
      }

      // # Reauthenticate user with current email
      const credential = EmailAuthProvider.credential(
        currentEmail,
        formData.emailCurrentPassword
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // # Call cloud function to update email
      const updateUserEmail = httpsCallable(functions, 'updateUserEmail');
      const result = await updateUserEmail({ newEmail: formData.newEmail });

      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to update email');
      }

      // # Clear form and show success message
      setFormData(prev => ({
        ...prev,
        emailCurrentPassword: '',
        newEmail: ''
      }));
      setEmailSuccess('Email updated successfully. Please verify your new email address.');
      setShowEmailModal(false);

    } catch (error) {
      console.error('Error updating email:', error);
      
      // # Handle specific Firebase errors
      if (error.code === 'auth/wrong-password') {
        setEmailError('Current password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setEmailError('Please sign in again before changing your email');
      } else if (error.code === 'auth/too-many-requests') {
        setEmailError('Too many attempts. Please try again later');
      } else if (error.code === 'auth/user-mismatch') {
        setEmailError('Authentication failed. Please sign out and sign in again.');
      } else if (error.code === 'auth/email-already-in-use' || 
                 (error.details?.code === 'already-exists')) {
        setEmailError('This email is already in use by another account');
      } else if (error.code === 'auth/invalid-email' || 
                 (error.details?.code === 'invalid-argument')) {
        setEmailError('The email address is not valid');
      } else if (error.message?.includes('subscriptions')) {
        setEmailError('Error updating subscriptions. Please try again or contact support.');
      } else {
        // # Get error message from either Firebase error or Cloud Function error
        const errorMessage = error.message || error.details?.message || 'Failed to update email';
        setEmailError(`${errorMessage}. Please try again.`);
      }
    } finally {
      setIsChangingEmail(false);
    }
  };

  // # Handle modal close
  const handleModalClose = () => {
    setShowEmailModal(false);
    setEmailError('');
    setFormData(prev => ({
      ...prev,
      emailCurrentPassword: ''
    }));
  };

  return (
    <div className={`sec_st_section ${isDarkMode ? 'stng_mn_dark-mode' : ''}`}>
      <div className="sec_st_header">
        <h2>Security Settings</h2>
      </div>

      <div className="sec_st_security-section">
        <h3>Change Password</h3>
        <form onSubmit={handlePasswordChange} className="sec_st_form">
          <div className="sec_st_form-grid">
            <div className="sec_st_form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                placeholder="Enter your current password"
              />
            </div>

            <div className="sec_st_form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                placeholder="Enter your new password"
              />
            </div>

            <div className="sec_st_form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm your new password"
              />
            </div>
          </div>

          <div className="sec_st_password-requirements">
            <h4>Password Requirements:</h4>
            <ul>
              <li>At least 8 characters long</li>
              <li>Contains uppercase and lowercase letters</li>
              <li>Contains at least one number</li>
              <li>Contains at least one special character (!@#$%^&*)</li>
            </ul>
          </div>

          {error && <div className="sec_st_error-message">{error}</div>}
          {success && <div className="sec_st_success-message">{success}</div>}

          <div className="sec_st_form-actions">
            <button 
              type="submit" 
              className="sec_st_save-button"
              disabled={isChangingPassword}
            >
              {isChangingPassword ? 'Updating Password...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>

      <div className="sec_st_security-section">
        <h3>Change Email</h3>
        <form onSubmit={handleEmailFormSubmit} className="sec_st_form">
          <div className="sec_st_form-grid">
            <div className="sec_st_form-group">
              <label>Current Email</label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="sec_st_disabled-input"
              />
            </div>

            <div className="sec_st_form-group">
              <label htmlFor="newEmail">New Email</label>
              <input
                type="email"
                id="newEmail"
                name="newEmail"
                value={formData.newEmail}
                onChange={handleChange}
                required
                placeholder="Enter your new email"
              />
            </div>
          </div>

          {emailError && <div className="sec_st_error-message">{emailError}</div>}
          {emailSuccess && <div className="sec_st_success-message">{emailSuccess}</div>}

          <div className="sec_st_form-actions">
            <button 
              type="submit" 
              className="sec_st_save-button"
              disabled={isChangingEmail}
            >
              Update Email
            </button>
          </div>
        </form>
      </div>

      {/* Email Change Confirmation Modal */}
      {showEmailModal && (
        <div className="sec_st_modal-overlay">
          <div className="sec_st_modal">
            <div className="sec_st_modal-header">
              <h3>Confirm Email Change</h3>
              <button 
                className="sec_st_modal-close"
                onClick={handleModalClose}
                type="button"
              >
                ×
              </button>
            </div>
            <div className="sec_st_modal-content">
              <p>Please enter your current password to confirm email change to:</p>
              <p className="sec_st_modal-email">{formData.newEmail}</p>
              <div className="sec_st_form-group">
                <label htmlFor="emailCurrentPassword">Current Password</label>
                <input
                  type="password"
                  id="emailCurrentPassword"
                  name="emailCurrentPassword"
                  value={formData.emailCurrentPassword}
                  onChange={handleChange}
                  required
                  placeholder="Enter your current password"
                  className="sec_st_modal-input"
                />
              </div>
              {emailError && <div className="sec_st_error-message">{emailError}</div>}
            </div>
            <div className="sec_st_modal-footer">
              <button 
                className="sec_st_modal-cancel"
                onClick={handleModalClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className="sec_st_modal-confirm"
                onClick={handleEmailChange}
                disabled={isChangingEmail}
              >
                {isChangingEmail ? 'Updating...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sec_st_security-section">
        <h3>Two-Factor Authentication</h3>
        <p className="sec_st_coming-soon">Coming soon...</p>
      </div>

      <div className="sec_st_security-section">
        <h3>Login History</h3>
        <div className="sec_st_login-history">
          {isLoadingHistory ? (
            <div className="sec_st_loading">
              <p>Loading login history...</p>
            </div>
          ) : historyError ? (
            <div className="sec_st_error-message">{historyError}</div>
          ) : loginHistory.length === 0 ? (
            <p className="sec_st_no-history">No login history available</p>
          ) : (
            <>
              <div className="sec_st_history-filters">
                <h4>Recent Activity</h4>
              </div>
              <div className="sec_st_history-list">
                {loginHistory.map((activity, index) => {
                  const deviceInfo = getDeviceInfo(activity.device.split(' - ')[1] || '');
                  return (
                    <div key={index} className="sec_st_history-item">
                      <div className="sec_st_history-icon">
                        {activity.type === 'sign_in' ? '→' : '←'}
                      </div>
                      <div className="sec_st_history-details">
                        <div className="sec_st_history-main">
                          <span className="sec_st_history-type">
                            {activity.type === 'sign_in' ? 'Sign In' : 'Sign Out'}
                          </span>
                          <span className="sec_st_history-device">
                            <span 
                              className="sec_st_device-type"
                              data-device={deviceInfo.deviceType}
                            >
                              {deviceInfo.deviceType}
                            </span>
                            <span 
                              className="sec_st_device-browser"
                              data-browser={deviceInfo.browser}
                            >
                              {deviceInfo.browser}
                            </span>
                          </span>
                        </div>
                        <div className="sec_st_history-meta">
                          <span className="sec_st_history-time">
                            {format(new Date(activity.timestamp), 'MMM d, yyyy HH:mm')}
                          </span>
                          <span className="sec_st_history-ip">
                            IP: {activity.ip_address}
                          </span>
                          {activity.timezone && (
                            <span className="sec_st_history-timezone">
                              {activity.timezone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="sec_st_history-relative-time">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="sec_st_history-footer">
                <p className="sec_st_history-note">
                  * Showing last 30 days of activity
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

SecuritySettings.propTypes = {
  isDarkMode: PropTypes.bool
};

SecuritySettings.defaultProps = {
  isDarkMode: false
};

export default SecuritySettings; 